/* Peak Moto - description tabs / accordions
   Pairs with sections/pmg-description.liquid.

   The markup is already complete and readable before this runs; all this does
   is decide what is shown. If it never loads, every section stays open, which
   is a worse layout but never a broken page.

   Desktop (>=750px): a tablist. One panel visible, arrow keys move between
   tabs, Home/End jump to the ends.
   Mobile (<750px): accordions, one open at a time (matching how the app was
   configured), height animated unless the viewer has asked for reduced
   motion. */

(function () {
  'use strict';

  var DESKTOP = '(min-width: 750px)';

  // Reading a layout property flushes pending style changes, which is what
  // makes the next height assignment animate instead of jumping.
  function forceLayout(el) { return el.offsetHeight; }

  /* Supplier copy arrives with its headings in every register, and a good
     number of them shout: SIZING, WASH & CARE, CE APPROVED. Sentence case
     reads better in a tab strip, but only where it is safe to apply.

     Two rules keep it safe. A label with any lowercase letter in it is
     already someone's deliberate choice and is left exactly as written --
     this only ever touches a label that is entirely uppercase. And inside
     one of those, short tokens and anything carrying a digit survive, so
     CE, AAA, H2O and D3O are not quietly turned into words.

     The cost of the length rule is that a genuinely shouted three-letter
     word stays shouted. That is the right way round: a missed FIT is a
     blemish, an "Aaa rated" is an error.

     This is a rule rather than a pass over the products because hundreds of
     them carry these headings, and the source copy stays untouched. */
  function sentenceCase(text) {
    if (!/[A-Za-z]/.test(text)) return text;
    if (/[a-z]/.test(text)) return text;
    var out = text.replace(/[A-Za-z0-9'’-]+/g, function (word) {
      if (word.length < 4) return word;
      if (/\d/.test(word)) return word;
      return word.toLowerCase();
    });
    // The FIRST character, not the first lowercase one. "CE APPROVED" comes
    // out of the pass above as "CE approved", whose first lowercase letter is
    // in the second word -- uppercasing that gave "CE Approved", which is
    // title case by accident.
    return out.replace(/[A-Za-z0-9]/, function (c) { return c.toUpperCase(); });
  }

  /* The one text node worth rewriting: an element whose visible text lives
     in a single place. A chevron or an icon alongside it is fine, because
     neither carries text; anything more tangled is left alone rather than
     risking somebody's markup. */
  function soleTextNode(el) {
    var walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, null);
    var found = null;
    var node;
    while ((node = walker.nextNode())) {
      if (!node.nodeValue.trim()) continue;
      if (found) return null;
      found = node;
    }
    return found;
  }

  function tidyLabel(el) {
    var node = soleTextNode(el);
    if (!node) return;
    var next = sentenceCase(node.nodeValue);
    if (next !== node.nodeValue) node.nodeValue = next;
  }

  function PmgDescription() {
    return Reflect.construct(HTMLElement, [], PmgDescription);
  }

  PmgDescription.prototype = Object.create(HTMLElement.prototype);
  PmgDescription.prototype.constructor = PmgDescription;
  Object.setPrototypeOf(PmgDescription, HTMLElement);

  PmgDescription.prototype.connectedCallback = function () {
    if (this._ready) return;
    this._ready = true;

    this.tabs = Array.prototype.slice.call(this.querySelectorAll('.pmg-desc__tab'));
    this.groups = Array.prototype.slice.call(this.querySelectorAll('.pmg-desc__group'));
    this.accs = Array.prototype.slice.call(this.querySelectorAll('.pmg-desc__acc'));
    if (!this.groups.length) return;

    // The tab strip on desktop and the accordion headings on a phone are the
    // same labels, so they are tidied together and cannot disagree.
    this.tabs.forEach(tidyLabel);
    this.accs.forEach(tidyLabel);

    var self = this;

    this.tabs.forEach(function (tab, i) {
      tab.addEventListener('click', function () { self.select(i); });
      tab.addEventListener('keydown', function (e) { self.onTabKey(e, i); });
    });

    this.accs.forEach(function (acc, i) {
      acc.addEventListener('click', function () { self.toggle(i); });
    });

    this.mq = window.matchMedia(DESKTOP);
    this.onChange = function () { self.sync(); };
    if (this.mq.addEventListener) this.mq.addEventListener('change', this.onChange);
    else this.mq.addListener(this.onChange);

    // A link straight to a section, e.g. #pmgpanel-abc-2
    var hash = window.location.hash.replace('#', '');
    if (hash) {
      var target = this.groups.findIndex(function (g) {
        var panel = g.querySelector('.pmg-desc__panel');
        return panel && panel.id === hash;
      });
      if (target > -1) {
        if (this.mq.matches) this.select(target);
        else this.open(target, true);
      }
    }

    this.sync();
  };

  PmgDescription.prototype.disconnectedCallback = function () {
    if (!this.mq) return;
    if (this.mq.removeEventListener) this.mq.removeEventListener('change', this.onChange);
    else this.mq.removeListener(this.onChange);
  };

  /* On desktop every panel is collapsed except the active one, and the
     accordion's inline height must be cleared so the tab layout is not stuck
     at a height the accordion animation left behind. */
  PmgDescription.prototype.sync = function () {
    var desktop = this.mq.matches;
    this.classList.toggle('is-desktop', desktop);

    this.groups.forEach(function (group) {
      var panel = group.querySelector('.pmg-desc__panel');
      if (!panel) return;
      if (desktop) {
        panel.style.height = '';
      } else if (group.classList.contains('is-open')) {
        panel.style.height = 'auto';
      } else {
        panel.style.height = '0px';
      }
    });

    if (desktop) {
      var active = this.groups.findIndex(function (g) { return g.classList.contains('is-active'); });
      this.select(active > -1 ? active : 0, true);
    }
  };

  PmgDescription.prototype.select = function (index, silent) {
    var self = this;
    this.groups.forEach(function (group, i) {
      group.classList.toggle('is-active', i === index);
    });
    this.tabs.forEach(function (tab, i) {
      var on = i === index;
      tab.classList.toggle('is-active', on);
      tab.setAttribute('aria-selected', on ? 'true' : 'false');
      tab.setAttribute('tabindex', on ? '0' : '-1');
    });
    if (!silent && this.tabs[index]) {
      this.tabs[index].focus();
      // keep the chosen tab in view when the strip scrolls
      if (this.tabs[index].scrollIntoView) {
        this.tabs[index].scrollIntoView({ block: 'nearest', inline: 'nearest' });
      }
    }
    self.active = index;
  };

  PmgDescription.prototype.onTabKey = function (e, i) {
    var last = this.tabs.length - 1;
    var next = null;
    if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = i === last ? 0 : i + 1;
    else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = i === 0 ? last : i - 1;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = last;
    if (next === null) return;
    e.preventDefault();
    this.select(next);
  };

  PmgDescription.prototype.toggle = function (index) {
    var group = this.groups[index];
    if (!group) return;
    if (group.classList.contains('is-open')) this.close(index);
    else this.open(index);
  };

  PmgDescription.prototype.open = function (index, instant) {
    var self = this;
    // The Tabs Studio app was set to allow_multiple_open: false, so keep the
    // same one-at-a-time behaviour rather than quietly changing it.
    this.groups.forEach(function (g, i) {
      if (i !== index && g.classList.contains('is-open')) self.close(i);
    });
    var group = this.groups[index];
    var panel = group.querySelector('.pmg-desc__panel');
    var acc = this.accs[index];
    group.classList.add('is-open');
    if (acc) acc.setAttribute('aria-expanded', 'true');
    if (!panel) return;

    if (instant || this.reduced()) {
      panel.style.height = 'auto';
      return;
    }

    var target = panel.scrollHeight;
    panel.style.height = '0px';
    forceLayout(panel);
    panel.style.height = target + 'px';

    // transitionend is the normal path; the timer is the safety net, because a
    // backgrounded tab never fires it and the panel would be stuck mid-height.
    var settled = false;
    var done = function () {
      if (settled) return;
      settled = true;
      panel.style.height = 'auto';
      panel.removeEventListener('transitionend', done);
      clearTimeout(panel._pmgTimer);
    };
    panel.addEventListener('transitionend', done);
    panel._pmgTimer = setTimeout(done, 420);
  };

  PmgDescription.prototype.close = function (index) {
    var group = this.groups[index];
    var panel = group.querySelector('.pmg-desc__panel');
    var acc = this.accs[index];
    group.classList.remove('is-open');
    if (acc) acc.setAttribute('aria-expanded', 'false');
    if (!panel) return;

    clearTimeout(panel._pmgTimer);

    if (this.reduced()) {
      panel.style.height = '0px';
      return;
    }

    // auto -> a real number -> zero, so the transition has something to animate
    // between. A forced reflow between the two, rather than rAF, because rAF is
    // paused in a background tab and the panel would stay open.
    panel.style.height = panel.scrollHeight + 'px';
    forceLayout(panel);
    panel.style.height = '0px';
  };

  PmgDescription.prototype.reduced = function () {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  };

  if (window.customElements && !customElements.get('pmg-description')) {
    customElements.define('pmg-description', PmgDescription);
  }
})();
